import { useRef, useState } from "react";
import { CloudUpload } from "@mui/icons-material";
import {
	BadgeDollarSign,
	ShieldCheck,
	Package2,
	Ruler,
	StickyNote,
	Box as BoxIcon,
	Calendar,
	CaseUpper
} from 'lucide-react';
import { Box, Button, Grid, styled, Typography } from "@mui/material";
import CustomLinearProgress from "../Loaders/CustomLinearProgress";
import { useTheme } from "../../contexts/ThemeContext";
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useAuth } from "../../contexts/AuthContext";
import ErrorModal from "../Modals/ErrorModal";
import { fetchWrapperNoAuth, SERVER } from "../../utils/helpers";

const csvFields = [
	{
		title: "SKU",
		icon: CaseUpper,
		required: true
	},
	{
		title: "Cost",
		icon: BadgeDollarSign,
	},
	{
		title: "Source",
		icon: ShieldCheck,
	},
	{
		title: "Qty",
		icon: Package2,
	},
	{
		title: "Length",
		icon: Ruler,
	},
	{
		title: "Width",
		icon: Ruler,
	},
	{
		title: "Height",
		icon: Ruler,
	},
	{
		title: "Weight",
		icon: Ruler,
	},
	{
		title: "Inner Pack",
		icon: BoxIcon
	},
	{
		title: "Additional Costs",
		icon: BadgeDollarSign
	},
	{
		title: "Note",
		icon: StickyNote
	}
]

const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1,
});

export default function GameCSVUpload() {
	const { theme } = useTheme();
	const { user, password } = useAuth();

	const [openErrorModal, setopenErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false);

	const fileInputRef = useRef(null);

	const styles = {
		optionBox: {
			display: 'flex',
			alignItems: 'center',
			mb: 1.5,
			bgcolor: `${theme.color.secondary}15`,
			p: 1,
			borderRadius: '20px',
		},
		optionText: {
			fontFamily: theme.fontFamily,
			color: theme.text.primary,
			fontSize: {
				xs: "80%",
				sm: "90%",
			},
			cursor: 'default'
		},
		optionIcon: {
			marginRight: 3,
			color: theme.color.primary,
		},
		optionTextOptional: {
			ml: 0.5,
			fontFamily: theme.fontFamily,
			fontSize: {
				xs: "70%",
				sm: "80%",
			},
		},
	}

	const normalizeData = (dataArray) => {
		return dataArray.map(item => {
			const normalizedItem = {};
			for (const key in item) {
				const newKey = key.trim().toLowerCase().replace(/\s+/g, "_");
				normalizedItem[newKey] = item[key];
			}
			return normalizedItem;
		});
	};

	const alertError = (msg) => {
		setErrorMessage(msg);
		setopenErrorModal(true);
	}

	const handleFileButtonClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileChange = (event) => {
		const files = event.target.files;

		if (files.length > 0) {
			setIsLoadingUploadFile(true);
			uploadCSV(files[0]);
		} else {
			setIsLoadingUploadFile(false);
		}

		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	function uploadCSV(file) {
		if (file.size > 10000000) {
			setIsLoadingUploadFile(false);
			alertError("Your file is larger than 10MB. Please consider splitting the Excel file and sending multiple scan requests.");
		} else {
			if (file) {
				const reader = new FileReader();

				reader.onload = function (e) {
					const fileName = file.name.toLowerCase();

					const requiredFields = csvFields
						.filter(f => f.required)
						.map(f => f.title);

					if (fileName.endsWith('.csv')) {
						const text = e.target.result;

						Papa.parse(text, {
							header: true,
							complete: (results) => {
								const parsedHeaders = results.meta.fields || [];

								const missingFields = requiredFields.filter(
									field => !parsedHeaders.includes(field)
								);

								if (missingFields.length > 0) {
									alertError(`Missing required column(s): ${missingFields.join(', ')}. Please upload a valid CSV file.`);
									setIsLoadingUploadFile(false);
									return;
								}

								const data = results.data;

								const validData = data.filter(row =>
									requiredFields.every(field => {
										const value = row[field];
										return value !== undefined && value !== null && value.toString().trim() !== "";
									})
								);

								if (validData.length === 0) {
									alertError("All rows are missing required values. Please check your CSV.");
									setIsLoadingUploadFile(false);
									return;
								}
								sendFile(validData);
							},
							error: (err) => {
								alertError(`CSV Parsing Error. Please upload a valid CSV file.`);
								setIsLoadingUploadFile(false);
							}
						});
					} else if (fileName.endsWith('.xlsx')) {
						const data = new Uint8Array(e.target.result);
						const workbook = XLSX.read(data, { type: 'array' });
						const firstSheetName = workbook.SheetNames[0];
						const worksheet = workbook.Sheets[firstSheetName];
						const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

						const parsedHeaders = json.length > 0 ? Object.keys(json[0]) : [];

						const missingFields = requiredFields.filter(
							field => !parsedHeaders.includes(field)
						);

						if (missingFields.length > 0) {
							alertError(`Missing required column(s): ${missingFields.join(", ")}. Please upload a valid XLSX file.`);
							setIsLoadingUploadFile(false);
							return;
						}

						const validData = json.filter(row =>
							requiredFields.every(field => {
								const value = row[field];
								return value !== undefined && value !== null && value.toString().trim() !== "";
							})
						);

						if (validData.length === 0) {
							alertError("All rows are missing required values. Please check your XLSX file.");
							setIsLoadingUploadFile(false);
							return;
						}
						sendFile(validData);
					} else {
						alertError("Unsupported file type. Please upload CSV or XLSX.");
						setIsLoadingUploadFile(false);
					}
				};

				if (file.name.toLowerCase().endsWith('.csv')) {
					reader.readAsText(file);
				} else if (file.name.toLowerCase().endsWith('.xlsx')) {
					reader.readAsArrayBuffer(file);
				}
			}
		}
	}

	const sendFile = async (data) => {
		const csvData = normalizeData(data);

		const options = {
			method: "POST",
			body: {
				csvFile: csvData,
			},
		};

		try {
			await fetchWrapperNoAuth(`${SERVER}/reports/upload_metadata`, user, password, options);
			alertError(`Successfully uploaded ${csvData.length} valid rows`);
		} catch (e) {
			alertError("Failed to upload CSV, Please try again or contact support.");
		} finally {
			setIsLoadingUploadFile(false);
		}
	}

	return <>
		<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
			<Typography
				variant="h6"
				sx={{
					color: theme.text.primary,
					fontFamily: theme.fontFamily,
					mb: 2,
					fontWeight: 600,
					fontSize: { xs: "80%", sm: "90%", md: "100%" },
				}}
			>
				The file should include the following:
			</Typography>

			<Box sx={{ mt: 1 }}>
				<Grid container spacing={1}>
					{csvFields.map(({ title, icon: Icon, required }) => (
						<Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
							<Box sx={styles.optionBox}>
								<Icon size={20} style={styles.optionIcon} />
								<Typography sx={styles.optionText}>
									{title}&nbsp;
									<Typography component="span" sx={{ ...styles.optionTextOptional, color: required ? theme.accent.error : theme.text.tertiary }}>
										({required ? "required" : "optional"})
									</Typography>
								</Typography>
							</Box>
						</Grid>
					))}
				</Grid>
			</Box>
			<Typography
				variant="h6"
				sx={{
					color: theme.text.primary,
					mb: "2vh",
					fontFamily: theme.fontFamily,
					fontSize: { xs: "100%", sm: "110%" },
				}}
			>
				Make sure you use our template or use the same column names!
			</Typography>
			<Typography
				variant="body2"
				sx={{
					color: "slateblue",
					mb: "2vh",
					fontFamily: theme.fontFamily,
					textDecoration: "underline",
					cursor: "pointer",
					fontSize: { xs: "90%", sm: "100%" },
				}}
				onClick={() => {
					const fields = csvFields.map(field => field.title).join(",");
					const csvContent = `data:text/csv;charset=utf-8,${fields}\n`;
					const encodedUri = encodeURI(csvContent);

					const link = document.createElement("a");
					link.href = encodedUri;
					link.download = "template.csv";
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				}}
			>
				Click here to download the template
			</Typography>
			<Box
				sx={{
					border: '2px dashed #ccc',
					padding: '16px',
					textAlign: 'center',
					width: { xs: "80%", sm: "50%", md: "50%" },
					borderRadius: '8px',
					cursor: 'pointer',
					margin: '15px auto',
				}}
			>
				<Button
					variant="contained"
					startIcon={<CloudUpload />}
					size="large"
					sx={{
						textTransform: 'none',
						fontFamily: theme.fontFamily,
						color: theme.text.negative,
						borderRadius: '10px',
						backgroundColor: theme.color.primary,
						border: `1px solid ${theme.border.light}`,
						fontSize: { xs: "90%", sm: "100%" },
					}}
					onClick={handleFileButtonClick}
					disabled={isLoadingUploadFile}
				>
					Choose a file
				</Button>

				<VisuallyHiddenInput ref={fileInputRef} type="file" onChange={handleFileChange} accept=".csv,.xlsx" />

				<Typography
					variant="body2"
					sx={{
						mt: '8px',
						fontFamily: theme.fontFamily,
						color: theme.text.primary,
						fontSize: { xs: "90%", sm: "100%" },
					}}
				>
					(Accept .csv and .xlsx files)
				</Typography>

				{isLoadingUploadFile ? (
					<CustomLinearProgress />
				) : (
					<Typography
						variant="caption"
						sx={{
							display: 'block',
							marginTop: '8px',
							fontFamily: theme.fontFamily,
							color: theme.text.primary,
							fontSize: { xs: "80%", sm: "90%" },
						}}
					>
						No file uploaded
					</Typography>
				)}
			</Box>
		</Box>
		<ErrorModal open={openErrorModal} setOpen={setopenErrorModal} message={errorMessage} />
	</>
}